import React, { useCallback, useRef, KeyboardEvent } from 'react';
import styled from 'styled-components';

// Helpers
import mq from '~/App/helpers/mq';
import { linkUnderline } from '~/App/helpers/mixins';
import { LoginView, loginViews } from '~/App/components/Header/constants';
import { useLoginModal } from '~/App/shared/hooks/use-login-modal';

const Wrapper = styled.ul`
  position: absolute;
  bottom: 0;
  display: flex;
  padding: 0 1rem;

  ${mq('≥mediumLarge')`
    padding: 0 2rem;
  `};
`;

type ItemProps = {
  alternateColor: boolean;
  active: boolean;
};

const Item = styled.li<ItemProps>`
  ${linkUnderline};
  color: ${({ alternateColor, theme }) =>
    alternateColor ? theme.themeColors.primary : theme.colors.white};
  border-bottom: ${({ active }) => active && '0.5rem solid currentColor'};

  ${mq<ItemProps>('≥mediumLarge')`
    &::after {
      height: 0.5rem;
      background: currentColor;
    }

    &:hover,
    &:focus,
    &:active {
      &::after {
        visibility: visible;
        transform: scaleX(1);
        display: ${({ active }) => (active ? 'none' : 'initial')};
      }
    }
  `};

  &:not(:first-child) {
    margin-left: 2rem;

    ${mq('≥mediumLarge')`
      margin-left: 3rem;
    `};
  }
`;

const Button = styled.button`
  border: none;
  background-color: inherit;
  color: inherit;
  margin: 0;
  padding: 1rem 0;
  font-size: 1rem;
  line-height: 1;
  text-transform: uppercase;
  cursor: pointer;

  &[aria-selected='true'] {
    font-weight: 700;
  }

  ${mq('≥mediumLarge')`
    font-size: 1.125rem;
  `};
`;

type Props = {
  alternateColor: boolean;
};

export function Navigation({ alternateColor }: Props) {
  const loginModal = useLoginModal();

  const setLoginView = useCallback(
    (view: LoginView) => () => {
      loginModal.handlers.setLoginView(view);
    },
    [loginModal.handlers]
  );

  const tabs = [
    {
      view: loginViews.private,
      text: 'Privat',
      ref: useRef<HTMLButtonElement>(null)
    },
    {
      view: loginViews.researcher,
      text: 'Forskare',
      ref: useRef<HTMLButtonElement>(null)
    },
    {
      view: loginViews.caregiver,
      text: 'Vårdpersonal',
      ref: useRef<HTMLButtonElement>(null)
    }
  ];

  const handleKeyPress = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    const lastTabIndex = tabs.length - 1;
    const nextIndex = index < lastTabIndex ? index + 1 : 0;
    const prevIndex = index > 0 ? index - 1 : lastTabIndex;

    if (event.key === 'ArrowLeft') {
      tabs[prevIndex].ref.current?.focus();
    }

    if (event.key === 'ArrowRight') {
      tabs[nextIndex].ref.current?.focus();
    }
  };

  return (
    <Wrapper role="tablist">
      {tabs.map((tab, index) => {
        const active = loginModal.loginView === tab.view;

        return (
          <Item
            alternateColor={alternateColor}
            active={active}
            key={tab.view}
            role="presentation"
          >
            <Button
              ref={tab.ref}
              role="tab"
              tabIndex={active ? undefined : -1}
              aria-selected={active}
              onClick={setLoginView(tab.view)}
              onKeyUp={(event) => handleKeyPress(event, index)}
            >
              {tab.text}
            </Button>
          </Item>
        );
      })}
    </Wrapper>
  );
}
